import ReactECharts from 'echarts-for-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LoadingState } from '../common';
import { useEventContext } from '../../contexts/EventContext';
import { useBasicStatisticsQuery } from '../../hooks/useBasicStatisticsQuery';
import { useEventCollectFlagsQuery } from '../../hooks/useEventCollectFlagsQuery';
import { useGraphCategoriesQuery } from '../../hooks/useGraphCategoriesQuery';
import { useLivePositionQuery } from '../../hooks/useLivePositionQuery';
import { useStoredRefreshInterval } from '../../hooks/useStoredRefreshInterval';
import { EVENT_LIST_ROUTE_PATH } from '../../routes/appRoutes';
import type { LivePositionItem } from '../../services/graphApi';
import { createLivePositionOption } from './livePositionChartOptions';
import { StatisticsRefreshControl } from './StatisticsRefreshControl';
import StatisticsFilterBar, {
  emptyStatisticsFilters,
  toGraphFilters,
  uniqueFilterValues,
} from './StatisticsFilterBar';

const refreshOptions = [
  { label: '수동', value: 0 },
  { label: '10초', value: 10000 },
  { label: '30초', value: 30000 },
  { label: '1분', value: 60000 },
];
const LIVE_POSITION_REFRESH_STORAGE_KEY = 'statistics.livePosition.refreshIntervalMs';

type LivePositionChartGroup = {
  categoryId: number;
  categoryName: string;
  positions: LivePositionItem[];
  participantCount: number;
};

function groupPositionsByCategory(positions: LivePositionItem[]) {
  const groupMap = new Map<number, LivePositionChartGroup>();

  positions.forEach((position) => {
    const group = groupMap.get(position.categoryId);
    if (group) {
      group.positions.push(position);
      group.participantCount += position.participantCount;
      return;
    }

    groupMap.set(position.categoryId, {
      categoryId: position.categoryId,
      categoryName: position.categoryName,
      positions: [position],
      participantCount: position.participantCount,
    });
  });

  return Array.from(groupMap.values());
}

function LivePositionDashboard() {
  const { selectedEventId } = useEventContext();
  const collectFlagsQuery = useEventCollectFlagsQuery(selectedEventId);
  const collectBirth = collectFlagsQuery.data?.collectBirth ?? true;
  const collectGender = collectFlagsQuery.data?.collectGender ?? true;
  const [filters, setFilters] = useState(emptyStatisticsFilters);
  // 백엔드 응답에 남아있는 값과 무관하게, 미수집 항목은 필터를 무조건 전체로 고정한다.
  const graphFilters = useMemo(() => {
    const base = toGraphFilters(filters);
    return {
      ...base,
      ageGroup: collectBirth ? base.ageGroup : '',
      gender: collectGender ? base.gender : '',
    };
  }, [filters, collectBirth, collectGender]);
  const {
    applyRefreshInterval,
    draftRefreshIntervalMs,
    refreshIntervalMs,
    setDraftRefreshIntervalMs,
  } = useStoredRefreshInterval(LIVE_POSITION_REFRESH_STORAGE_KEY);
  const categoryQuery = useGraphCategoriesQuery(selectedEventId);
  const filterOptionQuery = useBasicStatisticsQuery(selectedEventId);
  const livePositionQuery = useLivePositionQuery(selectedEventId, refreshIntervalMs, graphFilters);
  const categories = useMemo(() => categoryQuery.data ?? [], [categoryQuery.data]);
  const filterOptionAges = useMemo(
    () => filterOptionQuery.data?.ages ?? [],
    [filterOptionQuery.data?.ages]
  );
  const genderOptions = useMemo(
    () => uniqueFilterValues(filterOptionAges.map((age) => age.gender)),
    [filterOptionAges]
  );
  const ageGroupOptions = useMemo(
    () => uniqueFilterValues(filterOptionAges.map((age) => age.ageGroup)),
    [filterOptionAges]
  );
  const positions = useMemo(
    () => livePositionQuery.data?.positions ?? [],
    [livePositionQuery.data?.positions]
  );
  const hasChartData = positions.length > 0;
  const chartGroups = useMemo(
    () => groupPositionsByCategory(positions),
    [positions]
  );
  const updatedAt = Math.max(
    categoryQuery.dataUpdatedAt,
    livePositionQuery.dataUpdatedAt
  );
  const isPageLoading = categoryQuery.isLoading || livePositionQuery.isLoading;
  const isPageFetching = categoryQuery.isFetching || livePositionQuery.isFetching;
  const pageError = categoryQuery.error ?? livePositionQuery.error;
  const isPageError = categoryQuery.isError || livePositionQuery.isError;
  const errorMessage = pageError instanceof Error
    ? pageError.message
    : '실시간 참가자 위치 현황 데이터를 불러오지 못했습니다.';

  let chartContent: ReactNode = (
    <div className="live-position-chart-list">
      {chartGroups.map((group) => (
        <section
          className="live-position-chart-panel"
          aria-label={`${group.categoryName} 실시간 참가자 위치 현황 그래프`}
          key={group.categoryId}
        >
          <div className="live-position-chart-heading">
            <h2>{group.categoryName}</h2>
            <span>{group.participantCount.toLocaleString()}명</span>
          </div>
          <ReactECharts
            option={createLivePositionOption(group.positions, false)}
            notMerge
            style={{ height: 420, width: '100%' }}
          />
        </section>
      ))}
    </div>
  );

  if (selectedEventId == null) {
    chartContent = (
      <div className="live-position-message">
        선택된 대회가 없습니다. <Link to={EVENT_LIST_ROUTE_PATH}>대회 목록에서 선택</Link>해 주세요.
      </div>
    );
  } else if (isPageLoading) {
    chartContent = <LoadingState message="실시간 참가자 위치 현황 데이터를 불러오는 중입니다." />;
  } else if (!hasChartData) {
    chartContent = (
      <div className="live-position-message">표시할 실시간 참가자 위치 현황 데이터가 없습니다.</div>
    );
  }

  return (
    <div className="live-position-page">
      <div className="live-position-header">
        <div className="page-title-block">
          <p className="page-section">Statistics</p>
          <h1>실시간 참가자 위치 현황</h1>
          <p className="page-description">각 참가자의 마지막 통과 계측지점을 기준으로 현재 위치 분포를 확인합니다.</p>
        </div>
        <StatisticsRefreshControl
          ariaLabel="실시간 참가자 위치 현황 갱신 주기"
          className="live-position-refresh-control"
          draftRefreshIntervalMs={draftRefreshIntervalMs}
          isApplyDisabled={draftRefreshIntervalMs === refreshIntervalMs}
          isRefreshDisabled={isPageFetching}
          options={refreshOptions}
          updatedAt={updatedAt}
          onApply={applyRefreshInterval}
          onChange={setDraftRefreshIntervalMs}
          onRefresh={() => {
            void Promise.all([
              categoryQuery.refetch(),
              livePositionQuery.refetch(),
            ]);
          }}
        />
      </div>

      <StatisticsFilterBar
        filters={filters}
        categories={categories}
        genderOptions={genderOptions}
        ageGroupOptions={ageGroupOptions}
        disabled={isPageFetching}
        collectBirth={collectBirth}
        collectGender={collectGender}
        onChange={setFilters}
        onReset={() => setFilters(emptyStatisticsFilters)}
      />

      {isPageError && (
        <div className="live-position-message live-position-error" role="alert">
          {errorMessage}
        </div>
      )}

      {chartContent}
    </div>
  );
}

export default LivePositionDashboard;
